import { FormErrors } from '../types/auth.types';

export class ValidationUtils {
  static validateEmail(email: string): string | null {
    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    return null;
  }

  static validatePassword(password: string): string | null {
    if (!password) {
      return 'Password is required';
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }

    return null;
  }

  static validateConfirmPassword(
    password: string,
    confirmPassword: string
  ): string | null {
    if (!confirmPassword) {
      return 'Please confirm your password';
    }

    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }

    return null;
  }

  static validateRequired(value: string, fieldName: string): string | null {
    if (!value || value.trim().length === 0) {
      return `${fieldName} is required`;
    }
    return null;
  }

  static validateUsername(username: string): string | null {
    if (!username) {
      return null; // Username is optional
    }

    if (username.length < 3) {
      return 'Username must be at least 3 characters long';
    }

    if (username.length > 50) {
      return 'Username must not exceed 50 characters';
    }

    const usernameRegex = /^[a-zA-Z0-9_.-]+$/;
    if (!usernameRegex.test(username)) {
      return 'Username can only contain letters, numbers, underscores, periods, and hyphens';
    }

    return null;
  }

  static validateIdentifier(identifier: string): string | null {
    if (!identifier) {
      return 'Email or username is required';
    }

    // If it contains @, validate as email
    if (identifier.includes('@')) {
      return this.validateEmail(identifier);
    } else {
      // Validate as username
      if (identifier.length < 3) {
        return 'Username must be at least 3 characters long';
      }
      if (identifier.length > 50) {
        return 'Username must not exceed 50 characters';
      }
      const usernameRegex = /^[a-zA-Z0-9_.-]+$/;
      if (!usernameRegex.test(identifier)) {
        return 'Username can only contain letters, numbers, underscores, periods, and hyphens';
      }
    }

    return null;
  }

  static validateLoginForm(identifier: string, password: string): FormErrors {
    const errors: FormErrors = {};

    const identifierError = this.validateIdentifier(identifier);
    if (identifierError) errors['identifier'] = identifierError;

    const passwordError = this.validateRequired(password, 'Password');
    if (passwordError) errors['password'] = passwordError;

    return errors;
  }

  static validateRegisterForm(
    email: string,
    password: string,
    confirmPassword: string,
    firstName: string,
    lastName: string,
    username?: string
  ): FormErrors {
    const errors: FormErrors = {};

    const emailError = this.validateEmail(email);
    if (emailError) errors['email'] = emailError;

    const passwordError = this.validatePassword(password);
    if (passwordError) errors['password'] = passwordError;

    const confirmPasswordError = this.validateConfirmPassword(
      password,
      confirmPassword
    );
    if (confirmPasswordError) errors['confirm_password'] = confirmPasswordError;

    const firstNameError = this.validateRequired(firstName, 'First name');
    if (firstNameError) errors['first_name'] = firstNameError;

    const lastNameError = this.validateRequired(lastName, 'Last name');
    if (lastNameError) errors['last_name'] = lastNameError;

    if (username) {
      const usernameError = this.validateUsername(username);
      if (usernameError) errors['username'] = usernameError;
    }

    return errors;
  }

  static hasErrors(errors: FormErrors): boolean {
    return Object.keys(errors).some((key) => errors[key] !== undefined);
  }
}
